import { compareStatusHistoryEntries, getLatestStatusEntry } from './statusRemarks';
import { formatDisplayDate, parseDisplayDate } from './dateFormat';
import { resolveDashboardInterviewDateRange } from './requirementDateFilter';
import {
  isRequirementWorkBlocked,
  normalizeRequirementType,
} from './requirementType';
import { isPresentInterviewCandidate } from './candidateStatus';

export const isActiveOpenRequirement = (type) => {
  const normalized = normalizeRequirementType(type);
  return Boolean(normalized) && !isRequirementWorkBlocked(normalized);
};

export const getActiveOpenRequirements = (requirements = []) => (
  (Array.isArray(requirements) ? requirements : [])
    .filter((req) => isActiveOpenRequirement(req.requirementtype || req.RequirmentType))
);

export const getLatestCandidateStatus = (candidate) => {
  if (candidate?.currentStatus) return candidate.currentStatus;

  const latestEntry = getLatestStatusEntry(candidate?.Status);
  if (!latestEntry?.Status) return '';
  return latestEntry.Status;
};

/** Date of the chronologically latest Status-history entry with Status === 'Joined'. */
export const getJoinedStatusDate = (candidate) => {
  const history = Array.isArray(candidate?.Status) ? candidate.Status : [];
  const joinedEntries = history.filter((entry) => entry?.Status === 'Joined');
  if (!joinedEntries.length) return null;

  let latestJoined = joinedEntries[0];
  let latestIndex = 0;
  joinedEntries.forEach((entry, index) => {
    if (compareStatusHistoryEntries(entry, latestJoined, index, latestIndex) > 0) {
      latestJoined = entry;
      latestIndex = index;
    }
  });

  return latestJoined?.Date ?? null;
};

/** Admin Dashboard Joined metric: latest status Joined + Joined status date in range (not upload date). */
export const isAdminJoinedCandidateInDateRange = (candidate, from = '', to = '') => {
  if (getLatestCandidateStatus(candidate) !== 'Joined') return false;
  const joinedDate = getJoinedStatusDate(candidate);
  if (!joinedDate) return false;
  return isWithinDateRange(joinedDate, from, to);
};

function countAdminJoinedCandidates(candidates = [], from = '', to = '') {
  return candidates.filter((candidate) => isAdminJoinedCandidateInDateRange(candidate, from, to)).length;
}

/** Canonical stored status values for Admin Dashboard offer/onboard/decline cards. */
export const ADMIN_DASHBOARD_STATUS_METRICS = [
  { key: 'offerToBeReleased', status: 'Offer to be released', title: 'Offer To Be Released' },
  { key: 'offerReleased', status: 'Offer Released', title: 'Offer Released' },
  { key: 'offerAccepted', status: 'Offer Accepted', title: 'Offer Accepted' },
  { key: 'onboardConfirmation', status: 'Onboard Confirmation', title: 'Onboard Confirmation' },
  { key: 'candidateDeclined', status: 'Candidate Declined', title: 'Candidate Declined' },
];

/** Combined Admin Dashboard card: the six HR follow-up pipeline statuses. */
export const ADMIN_HR_FOLLOW_UP_STATUSES = [
  'Offer to be released',
  'Offer Released',
  'Offer Accepted',
  'Onboard Confirmation',
  'Candidate Declined',
  'Joined',
];

const ADMIN_HR_FOLLOW_UP_STATUS_SET = new Set(ADMIN_HR_FOLLOW_UP_STATUSES);

export const ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD = {
  key: 'hrFollowUp',
  title: 'HR Follow-up',
  icon: '🗂️',
  accent: 'teal',
};

export const ADMIN_DASHBOARD_STATUS_CARDS = [ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD];

/** Date of the chronologically latest Status-history entry with the given Status. */
export function getStatusHistoryDateForStatus(candidate, statusLabel) {
  const history = Array.isArray(candidate?.Status) ? candidate.Status : [];
  const matchingEntries = history.filter((entry) => entry?.Status === statusLabel);
  if (!matchingEntries.length) return null;

  let latestEntry = matchingEntries[0];
  let latestIndex = 0;
  matchingEntries.forEach((entry, index) => {
    if (compareStatusHistoryEntries(entry, latestEntry, index, latestIndex) > 0) {
      latestEntry = entry;
      latestIndex = index;
    }
  });

  return latestEntry?.Date ?? null;
}

/** Admin Dashboard status cards: latest status matches + status entry Date in range (not upload date). */
export function isAdminStatusMetricCandidateInDateRange(
  candidate,
  statusLabel,
  from = '',
  to = '',
) {
  if (getLatestCandidateStatus(candidate) !== statusLabel) return false;
  const statusDate = getStatusHistoryDateForStatus(candidate, statusLabel);
  if (!statusDate) return false;
  return isWithinDateRange(statusDate, from, to);
}

function countAdminStatusMetricCandidates(candidates, statusLabel, from, to) {
  return candidates.filter(
    (candidate) => isAdminStatusMetricCandidateInDateRange(candidate, statusLabel, from, to),
  ).length;
}

function filterAdminStatusMetricRows(rows, statusLabel, from, to) {
  return rows.filter((row) => isAdminStatusMetricCandidateInDateRange(
    row.candidate,
    statusLabel,
    from,
    to,
  ));
}

export function isAdminHrFollowUpCandidateInDateRange(candidate, from = '', to = '') {
  const latest = getLatestCandidateStatus(candidate);
  if (!ADMIN_HR_FOLLOW_UP_STATUS_SET.has(latest)) return false;
  return isAdminStatusMetricCandidateInDateRange(candidate, latest, from, to);
}

export function buildDashboardUserNameMap(userList = []) {
  const map = new Map();
  (Array.isArray(userList) ? userList : []).forEach((user) => {
    const id = String(user?._id || user?.id || '').trim();
    if (!id) return;
    map.set(id, user.EmployeeName || user.name || '—');
  });
  return map;
}

function getCandidateUploaderIds(candidate = {}) {
  const ids = [];
  const recruiterId = candidate?.recruiterId;
  if (Array.isArray(recruiterId)) {
    recruiterId.forEach((id) => {
      if (id != null && String(id).trim()) ids.push(String(id).trim());
    });
  } else if (recruiterId != null && String(recruiterId).trim()) {
    ids.push(String(recruiterId).trim());
  }
  return ids;
}

export function resolveUploaderEmployeeName(candidate, userNameMap) {
  const ids = getCandidateUploaderIds(candidate);
  if (userNameMap instanceof Map) {
    for (let i = 0; i < ids.length; i += 1) {
      const name = userNameMap.get(ids[i]);
      if (name) return name;
    }
  }
  if (candidate?.recruiterName) return candidate.recruiterName;
  if (candidate?.uploadedByName) return candidate.uploadedByName;
  return '—';
}

function uniqueDashboardCandidateRows(rows = []) {
  const seen = new Set();
  const unique = [];
  rows.forEach((row) => {
    const id = String(row?.candidateId || '').trim()
      || `${row?.candidateName || ''}|${row?.regId || ''}|${row?.requirementId || ''}`;
    if (seen.has(id)) return;
    seen.add(id);
    unique.push(row);
  });
  return unique;
}

export const normalizeClientsResponse = (clientsResponse) => {
  if (Array.isArray(clientsResponse)) return clientsResponse;
  if (clientsResponse?.clientUserCounts && Array.isArray(clientsResponse.clientUserCounts)) {
    return clientsResponse.clientUserCounts;
  }
  return [];
};

export const isWithinDateRange = (value, from = '', to = '') => {
  if (!from && !to) return true;

  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return false;

  if (from) {
    const start = new Date(from);
    start.setHours(0, 0, 0, 0);
    if (date < start) return false;
  }

  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (date > end) return false;
  }

  return true;
};

function toCalendarDateKey(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const isoPrefix = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoPrefix) {
    return `${isoPrefix[1]}-${isoPrefix[2]}-${isoPrefix[3]}`;
  }

  const parsed = parseDisplayDate(value);
  if (!parsed) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Dashboard interviews: filter by interviewDate within the selected date range (not upcoming-7-days). */
export function isInterviewDateInDashboardRange(interviewDate, from = '', to = '') {
  const dateKey = toCalendarDateKey(interviewDate);
  if (!dateKey) return false;
  if (from && dateKey < from) return false;
  if (to && dateKey > to) return false;
  return true;
}

function filterDashboardInterviewRows(rows = [], dateRange = {}) {
  const { from = '', to = '' } = resolveDashboardInterviewDateRange(dateRange);
  return rows.filter((row) => isInterviewDateInDashboardRange(row.candidate?.interviewDate, from, to));
}

/** Admin Dashboard Present Interviews: approved current status + existing interview-date range filter. */
function filterAdminPresentInterviewRows(rows = [], dateRange = {}) {
  const { from = '', to = '' } = resolveDashboardInterviewDateRange(dateRange);
  return rows.filter((row) => {
    if (!isPresentInterviewCandidate(row.candidate)) return false;
    return isInterviewDateInDashboardRange(row.candidate?.interviewDate, from, to);
  });
}

export const filterRequirementsByDateRange = (requirements = [], from = '', to = '', dateKey = 'startDate') => {
  if (!from && !to) return requirements;
  return requirements.filter((req) => isWithinDateRange(req?.[dateKey], from, to));
};

export const filterCandidatesByDateRange = (candidates = [], from = '', to = '', dateKey = 'uploadedOn') => {
  if (!from && !to) return candidates;
  return candidates.filter((candidate) => {
    const dateValue = candidate?.[dateKey] || candidate?.date;
    return isWithinDateRange(dateValue, from, to);
  });
};

export const getUploadedCandidatesFromRequirements = (requirements = []) => {
  const candidates = [];
  requirements.forEach((req) => {
    (req.uploadedCandidates || []).forEach((candidate) => {
      candidates.push(candidate);
    });
  });
  return candidates;
};

export const getUploadedCandidatesWithContext = (requirements = []) => {
  const rows = [];
  (Array.isArray(requirements) ? requirements : []).forEach((req) => {
    (req.uploadedCandidates || []).forEach((candidate) => {
      rows.push({
        candidate,
        client: req.client || req.ClientName || '—',
        role: candidate.role || req.role || req.Role || '—',
        regId: req.regId || '—',
        requirementId: req._id?.toString?.() || req._id || '',
      });
    });
  });
  return rows;
};

function formatInterviewDateLabel(candidate) {
  if (!candidate?.interviewDate) return '—';
  const date = new Date(candidate.interviewDate);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 1971) return '—';
  const label = formatDisplayDate(candidate.interviewDate);
  return candidate.interviewTime ? `${label} (${candidate.interviewTime})` : label;
}

function formatDashboardCandidateRow(row, userNameMap) {
  const { candidate, client, role, regId, requirementId } = row;
  const name = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
  const status = getLatestCandidateStatus(candidate) || 'No Action Taken';
  const statusDate = getStatusHistoryDateForStatus(candidate, status);

  return {
    candidateId: candidate._id?.toString?.() || candidate._id || '',
    candidateName: name || '—',
    employeeName: resolveUploaderEmployeeName(candidate, userNameMap),
    client,
    role,
    regId,
    requirementId,
    status,
    statusDate,
    statusDateLabel: statusDate ? formatDisplayDate(statusDate) : '—',
    interviewDateLabel: formatInterviewDateLabel(candidate),
  };
}

function collectAdminHrFollowUpRows(rows, from, to, userNameMap) {
  const matching = rows.filter((row) => isAdminHrFollowUpCandidateInDateRange(row.candidate, from, to));
  return uniqueDashboardCandidateRows(matching.map((row) => formatDashboardCandidateRow(row, userNameMap)));
}

export const computeCandidatePipelineStats = (candidates = [], from = '', to = '') => {
  const filtered = filterCandidatesByDateRange(candidates, from, to);
  const interviewRange = resolveDashboardInterviewDateRange({ from, to });
  let interviews = 0;
  let offers = 0;
  let joined = 0;

  candidates.forEach((candidate) => {
    if (isInterviewDateInDashboardRange(
      candidate?.interviewDate,
      interviewRange.from,
      interviewRange.to
    )) interviews += 1;
  });

  filtered.forEach((candidate) => {
    const latest = getLatestCandidateStatus(candidate);
    if (latest === 'Offer Released') offers += 1;
    if (latest === 'Joined') joined += 1;
  });

  return {
    candidates: filtered.length,
    interviews,
    offers,
    joined,
  };
};

export const computeAdminDashboardDrillDown = (requirements = [], dateRange = {}, userList = []) => {
  const { from = '', to = '' } = dateRange;
  const userNameMap = buildDashboardUserNameMap(userList);
  const rows = getUploadedCandidatesWithContext(requirements);
  const sourceProfileRows = rows.filter((row) =>
    isWithinDateRange(row.candidate?.uploadedOn || row.candidate?.date, from, to)
  );

  const statusDrillDown = ADMIN_DASHBOARD_STATUS_METRICS.reduce((acc, { key, status }) => {
    acc[key] = filterAdminStatusMetricRows(rows, status, from, to)
      .map((row) => formatDashboardCandidateRow(row, userNameMap));
    return acc;
  }, {});

  const hrFollowUp = collectAdminHrFollowUpRows(rows, from, to, userNameMap);

  return {
    sourceProfiles: sourceProfileRows.map((row) => formatDashboardCandidateRow(row, userNameMap)),
    interviews: filterAdminPresentInterviewRows(rows, dateRange)
      .map((row) => formatDashboardCandidateRow(row, userNameMap)),
    ...statusDrillDown,
    joined: rows
      .filter((row) => isAdminJoinedCandidateInDateRange(row.candidate, from, to))
      .map((row) => formatDashboardCandidateRow(row, userNameMap)),
    hrFollowUp,
    openRequirements: getActiveOpenRequirements(requirements).map(formatRequirementDrillDownRow),
  };
};

function collectTeamLeadCandidateRows(assignedRequirements = [], teamCountResponse = {}) {
  const rowByKey = new Map();

  (Array.isArray(assignedRequirements) ? assignedRequirements : []).forEach((row) => {
    const req = row.requirementDetails || {};
    (row.totalCandidatesDetails || []).forEach((candidate) => {
      if (candidate?.savedStatus !== 'Uploaded') return;
      const key = candidate._id
        || `${candidate.email || ''}-${candidate.mobileNumber || ''}-${candidate.uploadedOn || ''}`;
      if (!key) return;
      rowByKey.set(key, {
        candidate,
        client: req.client || '—',
        role: candidate.role || req.role || '—',
        regId: req.regId || '—',
        requirementId: req._id?.toString?.() || req._id || '',
      });
    });
  });

  (Array.isArray(teamCountResponse?.totalCandidatesData) ? teamCountResponse.totalCandidatesData : [])
    .forEach((candidate) => {
      if (candidate?.savedStatus !== 'Uploaded') return;
      const key = candidate._id
        || `${candidate.email || ''}-${candidate.mobileNumber || ''}-${candidate.uploadedOn || ''}`;
      if (!key || rowByKey.has(key)) return;
      rowByKey.set(key, {
        candidate,
        client: candidate.client || '—',
        role: candidate.role || '—',
        regId: candidate.regId || '—',
        requirementId: candidate.reqId || '',
      });
    });

  return Array.from(rowByKey.values());
}

export const computeTeamLeadDashboardDrillDown = (
  assignedRequirements = [],
  teamCountResponse = {},
  dateRange = {},
) => {
  const { from = '', to = '' } = dateRange;
  const rows = collectTeamLeadCandidateRows(assignedRequirements, teamCountResponse);
  const sourceProfileRows = rows.filter((row) =>
    isWithinDateRange(row.candidate?.uploadedOn || row.candidate?.date, from, to)
  );

  return {
    sourceProfiles: sourceProfileRows.map(formatDashboardCandidateRow),
    interviews: filterDashboardInterviewRows(rows, dateRange).map(formatDashboardCandidateRow),
    offers: sourceProfileRows
      .filter((row) => getLatestCandidateStatus(row.candidate) === 'Offer Released')
      .map(formatDashboardCandidateRow),
    joined: sourceProfileRows
      .filter((row) => getLatestCandidateStatus(row.candidate) === 'Joined')
      .map(formatDashboardCandidateRow),
  };
};

export const computeAdminDashboardStats = (
  requirements = [],
  clientsResponse = {},
  userList = [],
  dateRange = {}
) => {
  const { from = '', to = '' } = dateRange;
  const clients = normalizeClientsResponse(clientsResponse);
  const reqList = filterRequirementsByDateRange(
    Array.isArray(requirements) ? requirements : [],
    from,
    to,
    'startDate'
  );
  const users = Array.isArray(userList) ? userList : [];
  const contextRows = getUploadedCandidatesWithContext(requirements);

  const allUploadedCandidates = getUploadedCandidatesFromRequirements(requirements);
  const pipeline = computeCandidatePipelineStats(allUploadedCandidates, from, to);
  const interviews = filterAdminPresentInterviewRows(contextRows, dateRange).length;

  const openRequirements = getActiveOpenRequirements(requirements).length;
  const joined = countAdminJoinedCandidates(allUploadedCandidates, from, to);
  const statusCounts = ADMIN_DASHBOARD_STATUS_METRICS.reduce((acc, { key, status }) => {
    acc[key] = countAdminStatusMetricCandidates(allUploadedCandidates, status, from, to);
    return acc;
  }, {});
  const hrFollowUp = collectAdminHrFollowUpRows(contextRows, from, to, buildDashboardUserNameMap(users)).length;

  return {
    users: users.length,
    teamLeads: users.filter((u) => u.UserType === 'TeamLead').length,
    recruiters: users.filter((u) => u.UserType === 'User').length,
    admins: users.filter((u) => u.UserType === 'Admin').length,
    clients: clients.length,
    requirements: reqList.length,
    openRequirements,
    candidates: pipeline.candidates,
    interviews,
    ...statusCounts,
    joined,
    hrFollowUp,
  };
};

export const computeTeamLeadDashboardStats = (
  assignedRequirements = [],
  teamCountResponse = {},
  dateRange = {}
) => {
  const { from = '', to = '' } = dateRange;

  const requirementRows = Array.isArray(assignedRequirements) ? assignedRequirements : [];
  const assignedActiveRequirements = requirementRows.filter((row) => {
    const requirementType = row?.requirementDetails?.requirementtype || row?.requirementDetails?.RequirmentType;
    return isActiveOpenRequirement(requirementType);
  });

  const contextRows = collectTeamLeadCandidateRows(requirementRows, teamCountResponse);
  const scopedCandidates = contextRows.map((row) => row.candidate);

  const pipeline = computeCandidatePipelineStats(scopedCandidates, from, to);
  const interviews = filterDashboardInterviewRows(contextRows, dateRange).length;

  return {
    requirements: assignedActiveRequirements.length,
    candidates: pipeline.candidates,
    interviews,
    offers: pipeline.offers,
    joined: pipeline.joined,
  };
};

export const computeRecruiterDashboardStats = (
  requirements = [],
  workLogProfiles = [],
  dateRange = {}
) => {
  const { from = '', to = '' } = dateRange;
  const assignedActiveRequirements = (Array.isArray(requirements) ? requirements : [])
    .filter((req) => isActiveOpenRequirement(req.requirementtype || req.RequirmentType));

  const pipelineCandidates = (Array.isArray(workLogProfiles) ? workLogProfiles : []).map((profile) => ({
    uploadedOn: profile?.uploadedOn,
    interviewDate: profile?.interviewDate,
    Status: profile?.status ? [{ Status: profile.status, Date: profile.uploadedOn }] : [],
    offerInHand: profile?.offerInHand,
  }));

  const pipeline = computeCandidatePipelineStats(pipelineCandidates, from, to);

  return {
    requirements: assignedActiveRequirements.length,
    uploaded: pipeline.candidates,
    interviews: pipeline.interviews,
    offers: pipeline.offers,
    joined: pipeline.joined,
    followUps: 0,
  };
};

export const formatRequirementSummary = (req) => ({
  id: req._id,
  regId: req.regId || '',
  role: req.role || req.Role || req.regId || '—',
  client: req.client || req.ClientName || '—',
  type: normalizeRequirementType(req.requirementtype || req.RequirmentType) || '—',
});

export const formatRequirementDrillDownRow = (req) => ({
  requirementId: req._id?.toString?.() || req._id || '',
  regId: req.regId || '—',
  client: req.client || req.ClientName || '—',
  role: req.role || req.Role || '—',
  requirementType: normalizeRequirementType(req.requirementtype || req.RequirmentType) || '—',
  startDate: req.startDate || null,
});
